import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Play, GitCompareArrows, ShieldCheck, ShieldX, Send, Bot, Sparkles } from "lucide-react";
import { api, useFetch } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, Panel, StatusChip, LoadingState, EmptyState, PersonaStamp } from "@/components/app/primitives";
import { LiveBadge, ConfidenceRank } from "@/components/app/livePrimitives";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TRIAD_TONE = { pending_triad: "neutral", in_review: "risk", validated: "validated", rejected: "danger" };
const TRIAD_LABEL = { pending_triad: "Pending Triad", in_review: "In Triad review", validated: "Validated", rejected: "Rejected" };
const APPROVERS = ["six_lead", "sponsor"];

const FindingCard = ({ f, onTransition }) => {
  const { roleKey } = useApp();
  const canApprove = APPROVERS.includes(roleKey);
  return (
    <div className="rounded-xl border border-[--hairline] bg-[--shell-2] p-4" data-testid={`live-finding-${f.finding_id}`}>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <ConfidenceRank rank={f.rank} />
        <StatusChip tone={TRIAD_TONE[f.triad_status]} data-testid={`finding-triad-status-${f.finding_id}`}>{TRIAD_LABEL[f.triad_status]}</StatusChip>
      </div>
      <h4 className="font-display text-[13px] font-semibold text-[--tx-1] mb-1.5">{f.title}</h4>
      <p className="text-[12px] text-[--tx-2] leading-relaxed mb-3">{f.detail}</p>

      <div className="grid md:grid-cols-2 gap-1.5 text-[11px] mb-2">
        {[["Population", f.population], ["Comparison group", f.comparison_group],
          ["What could disprove it", f.disproof], ["Recommended next test", f.next_test],
          ["Candidate owner", f.candidate_owner]].filter(([, v]) => v).map(([l, v]) => (
          <div key={l}>
            <div className="text-[9px] font-mono uppercase tracking-wider text-[--tx-3]">{l}</div>
            <div className="text-[--tx-2] leading-snug">{v}</div>
          </div>
        ))}
      </div>

      {f.recommended_action && (
        <div className="rounded-lg border border-[--divider] bg-[--panel-dark] px-3 py-2 mb-2">
          <div className="text-[9px] font-mono uppercase tracking-wider text-[--green]">Recommended action (evidence threshold met)</div>
          <div className="text-[11px] text-[--tx-2] mt-0.5">{f.recommended_action}</div>
        </div>
      )}
      {f.data_request && (
        <div className="rounded-lg border border-[--amber] bg-[--amber-soft] px-3 py-2 mb-2">
          <div className="text-[9px] font-mono uppercase tracking-wider text-[--amber]">Exact data request</div>
          <div className="text-[11px] text-[--tx-2] mt-0.5">{f.data_request}</div>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-[--divider]">
        <div className="text-[9px] font-mono text-[--tx-3] min-w-0 truncate">
          {(f.evidence_refs || []).filter(Boolean).join(" · ")}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {f.triad_status === "pending_triad" && (
            <button onClick={() => onTransition(f, "in_review")} data-testid={`finding-send-triad-${f.finding_id}`}
              className="inline-flex items-center gap-1 rounded-lg border border-[--hairline] bg-[--shell-3] px-2.5 py-1.5 text-[11px] font-semibold text-[--tx-2] hover:text-[--tx-1] transition-colors">
              <Send size={10} /> Send to Triad
            </button>
          )}
          {f.triad_status === "in_review" && (
            <>
              <button onClick={() => onTransition(f, "validated")} disabled={!canApprove} data-testid={`finding-validate-${f.finding_id}`}
                title={canApprove ? "" : "Only SIX Lead or Executive Sponsor can validate"}
                className="inline-flex items-center gap-1 rounded-lg bg-[--green] hover:brightness-110 disabled:opacity-40 px-2.5 py-1.5 text-[11px] font-semibold text-white transition-colors">
                <ShieldCheck size={10} /> Validate
              </button>
              <button onClick={() => onTransition(f, "rejected")} disabled={!canApprove} data-testid={`finding-reject-${f.finding_id}`}
                className="inline-flex items-center gap-1 rounded-lg border border-[--danger] text-[--danger] hover:bg-[--danger-soft] disabled:opacity-40 px-2.5 py-1.5 text-[11px] font-semibold transition-colors">
                <ShieldX size={10} /> Reject
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default function LiveAgentRunsPage() {
  const { roleKey } = useApp();
  const { data: runs, refetch: refetchRuns } = useFetch("/live/agent-runs");
  const [running, setRunning] = useState(false);
  const [activeRunId, setActiveRunId] = useState(null);
  const [findings, setFindings] = useState(null);
  const [compareId, setCompareId] = useState(null);
  const [compareResult, setCompareResult] = useState(null);
  const [interp, setInterp] = useState(null);
  const [interpBusy, setInterpBusy] = useState(false);

  const latestRun = runs?.[0];
  const runId = activeRunId || latestRun?.id;

  useEffect(() => {
    if (!runId) { setFindings([]); return; }
    api.get(`/live/agent-runs/${runId}/findings`).then((r) => setFindings(r.data)).catch(() => setFindings([]));
  }, [runId]);

  const executeRun = async () => {
    setRunning(true);
    try {
      const res = await api.post("/live/agent-runs", { actor_role: roleKey });
      toast.success(`Live run complete - ${res.data.finding_count} findings`);
      setActiveRunId(res.data.id);
      refetchRuns();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Run failed - run a calculation pass first");
    }
    setRunning(false);
  };

  const transition = async (f, status) => {
    try {
      await api.patch(`/live/findings/${f.finding_id}`, { triad_status: status, actor_role: roleKey });
      toast.success(status === "in_review" ? "Routed to Triad review" : `Finding ${status}`);
      const r = await api.get(`/live/agent-runs/${runId}/findings`);
      setFindings(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Transition failed");
    }
  };

  const runCompare = async () => {
    if (!compareId || !runId || compareId === runId) return;
    try {
      const res = await api.get(`/live/agent-runs/compare?run_a=${compareId}&run_b=${runId}`);
      setCompareResult(res.data);
    } catch (e) { toast.error("Comparison failed"); }
  };

  const interpret = async () => {
    if (!runId) return;
    setInterpBusy(true);
    try {
      const res = await api.post(`/live/agent-runs/${runId}/interpret`, { actor_role: roleKey });
      setInterp(res.data);
    } catch (e) { toast.error("Interpretation failed"); }
    setInterpBusy(false);
  };

  useEffect(() => {
    setInterp((runs || []).find((r) => r.id === runId)?.ai_interpretation || null);
  }, [runId, runs]);

  const byAgent = useMemo(() => {
    const groups = {};
    (findings || []).forEach((f) => {
      groups[f.agent_id] = groups[f.agent_id] || { name: f.agent_name, items: [] };
      groups[f.agent_id].items.push(f);
    });
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [findings]);

  const activeRun = (runs || []).find((r) => r.id === runId);

  return (
    <div data-testid="live-runs-page" className="space-y-5">
      <PageHeader
        kicker="Live engagement · agentic analysis"
        title="Agentic Analysis Runs"
        subtitle="The twelve agents run against computed results from ingested Brightspeed data. Every finding carries evidence lineage and confidence; nothing publishes to the Command Center without passing the Triad Review gate."
        right={
          <div className="flex items-center gap-2">
            <LiveBadge />
            <button onClick={executeRun} disabled={running} data-testid="live-run-execute-button"
              className="inline-flex items-center gap-1.5 rounded-lg bg-[--six-crimson] hover:bg-[--six-crimson-hover] disabled:opacity-50 active:scale-[0.98] px-3.5 py-2 text-xs font-semibold text-white transition-colors">
              <Play size={12} /> {running ? "Agents working..." : "Execute live run"}
            </button>
          </div>
        }
      />

      {(runs || []).length === 0 ? (
        <Panel testId="live-runs-empty">
          <EmptyState message="No live runs yet. Execute the first run - the agents will work from the latest calculation pass." />
        </Panel>
      ) : (
        <>
          <Panel title="Run history" testId="live-run-history-panel"
            right={
              <div className="flex items-center gap-2">
                <Select value={compareId || ""} onValueChange={setCompareId}>
                  <SelectTrigger className="w-[210px] h-8 text-xs bg-[--shell-3] border-[--hairline]" data-testid="run-compare-select">
                    <SelectValue placeholder="Compare against..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[--shell-2] border-[--hairline]">
                    {(runs || []).filter((r) => r.id !== runId).map((r) => (
                      <SelectItem key={r.id} value={r.id} className="text-xs">{r.id} · {(r.ts || "").slice(5, 16).replace("T", " ")}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <button onClick={runCompare} disabled={!compareId} data-testid="run-compare-button"
                  className="inline-flex items-center gap-1 rounded-lg border border-[--hairline] bg-[--shell-3] px-2.5 py-1.5 text-[11px] font-semibold text-[--tx-2] hover:text-[--tx-1] disabled:opacity-40 transition-colors">
                  <GitCompareArrows size={11} /> Compare
                </button>
              </div>
            }>
            <div className="flex gap-2 overflow-x-auto thin-scroll pb-1">
              {(runs || []).map((r) => (
                <button key={r.id} onClick={() => { setActiveRunId(r.id); setCompareResult(null); }} data-testid={`run-chip-${r.id}`}
                  className={`shrink-0 rounded-lg border px-3 py-2 text-left transition-colors ${r.id === runId ? "border-[--ai] bg-[--ai-soft]" : "border-[--hairline] bg-[--shell-2] hover:border-[--ai]"}`}>
                  <div className="text-[11px] font-mono text-[--tx-1]">{r.id}</div>
                  <div className="text-[10px] text-[--tx-3]">{(r.ts || "").slice(0, 16).replace("T", " ")} · {r.finding_count} findings · calc {r.calc_version}</div>
                </button>
              ))}
            </div>
            {activeRun && (
              <div className="flex flex-wrap items-center gap-2 mt-2 pt-2 border-t border-[--divider]">
                {Object.entries(activeRun.counts_by_rank || {}).map(([rank, n]) => (
                  <span key={rank} className="inline-flex items-center gap-1.5"><ConfidenceRank rank={rank} /><span className="text-[11px] text-[--tx-2] tabular-nums">{n}</span></span>
                ))}
                <span className="text-[10px] font-mono text-[--tx-3] ml-auto">{(activeRun.inputs || []).length} input files · dated record {activeRun.id}</span>
              </div>
            )}
          </Panel>

          {compareResult && (
            <Panel title={`Run comparison: ${compareResult.run_a} → ${compareResult.run_b}`} testId="run-compare-panel">
              <div className="grid md:grid-cols-3 gap-3">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-[--green] mb-1.5">New findings ({compareResult.added.length})</div>
                  {compareResult.added.length === 0 && <div className="text-[11px] text-[--tx-3]">None</div>}
                  {compareResult.added.map((f) => <div key={f.finding_id} className="text-[11px] text-[--tx-2] mb-1">+ {f.title}</div>)}
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-[--tx-3] mb-1.5">Resolved ({compareResult.removed.length})</div>
                  {compareResult.removed.length === 0 && <div className="text-[11px] text-[--tx-3]">None</div>}
                  {compareResult.removed.map((f) => <div key={f.finding_id} className="text-[11px] text-[--tx-2] mb-1">− {f.title}</div>)}
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-[--ai] mb-1.5">Confidence changes ({compareResult.rank_changes.length})</div>
                  {compareResult.rank_changes.length === 0 && <div className="text-[11px] text-[--tx-3]">None</div>}
                  {compareResult.rank_changes.map((c, i) => (
                    <div key={i} className="text-[11px] text-[--tx-2] mb-1">{c.title}: <span className="font-mono">{c.from_rank} → {c.to_rank}</span></div>
                  ))}
                </div>
              </div>
            </Panel>
          )}

          <div className="rounded-xl border border-[--divider] bg-[--shell-2] px-4 py-3 flex items-center gap-3" data-testid="live-runs-governance-note">
            <div className="flex -space-x-1"><PersonaStamp code="TP" /><PersonaStamp code="DS" /><PersonaStamp code="TO" /></div>
            <p className="text-[11px] text-[--tx-2] leading-snug flex-1">
              No agent publishes independently. Findings marked <span className="text-[--green] font-semibold">Validated</span> by the Triad (SIX Lead or Executive Sponsor sign-off) are the only live findings surfaced to the Command Center.
            </p>
            <button onClick={interpret} disabled={interpBusy || !runId} data-testid="run-interpret-button"
              className="inline-flex items-center gap-1.5 rounded-lg border border-[--ai] text-[--ai] hover:bg-[--ai-soft] disabled:opacity-40 px-3 py-2 text-[11px] font-semibold transition-colors shrink-0">
              <Sparkles size={11} /> {interpBusy ? "Interpreting..." : "AI interpretation"}
            </button>
          </div>

          {interp && (
            <Panel title="AI interpretation of this run" testId="run-interpretation-panel"
              right={<StatusChip tone="ai">gpt-5.4 · explanatory only</StatusChip>}>
              <div className="rounded-lg border border-[--amber] bg-[--amber-soft] px-3 py-1.5 text-[10px] text-[--amber] font-semibold mb-3">
                {interp.label}
              </div>
              <pre className="text-[12px] text-[--tx-2] whitespace-pre-wrap leading-relaxed font-body" data-testid="run-interpretation-text">{interp.text}</pre>
            </Panel>
          )}

          {findings === null && <LoadingState message="Loading findings..." />}
          {byAgent.map(([agentId, group]) => (
            <div key={agentId} data-testid={`live-agent-group-${agentId.toLowerCase()}`}>
              <div className="flex items-center gap-2 mb-2">
                <Bot size={13} className="text-[--ai]" />
                <span className="font-mono text-[10px] text-[--tx-3]">{agentId}</span>
                <h3 className="font-display text-sm font-semibold text-[--tx-1]">{group.name}</h3>
                <span className="text-[10px] font-mono text-[--tx-3]">{group.items.length} finding{group.items.length > 1 ? "s" : ""}</span>
              </div>
              <div className="grid lg:grid-cols-2 gap-3 mb-4">
                {group.items.map((f) => <FindingCard key={f.finding_id} f={f} onTransition={transition} />)}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
